import React from 'react';
import { Box, Typography, Container, Grid, Button, Chip, Avatar } from '@mui/material';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTheme } from '../../../ThemeContext';

// Icons for Services
import VideocamIcon from '@mui/icons-material/Videocam';
import PeopleIcon from '@mui/icons-material/People';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import StarsRoundedIcon from '@mui/icons-material/StarsRounded';
import MovieCreationRoundedIcon from '@mui/icons-material/MovieCreationRounded';

// Icons for Bottom Features
import PublicIcon from '@mui/icons-material/Public';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import BoltIcon from '@mui/icons-material/Bolt';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import SensorsRoundedIcon from '@mui/icons-material/SensorsRounded';

const bottomFeatures = [
  {
    id: 'community',
    icon: <PublicIcon sx={{ fontSize: 26 }} />,
    title: 'Global Community',
    desc: 'Join creators and fans from 100+ countries.',
    color: '#07a4f5',
    gradient: 'linear-gradient(135deg, #07a4f5 0%, #38bdf8 100%)',
    badge: '100+ Nations',
    avatars: [
      { bg: '#07a4f5', label: 'R' },
      { bg: '#ec4899', label: 'J' },
      { bg: '#f59e0b', label: 'K' },
      { bg: '#10b981', label: 'M' },
    ],
    metricText: '100K+ Active Members',
  },
  {
    id: 'security',
    icon: <VerifiedUserIcon sx={{ fontSize: 26 }} />,
    title: 'Verified & Secure',
    desc: 'Safe platform with verified profiles and content.',
    color: '#10b981',
    gradient: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
    badge: '100% Verified',
    secBadge: '256-Bit Encrypted',
    metricText: 'Verified Industry ID',
  },
  {
    id: 'speed',
    icon: <BoltIcon sx={{ fontSize: 26 }} />,
    title: 'Fast & Reliable',
    desc: 'Smooth streaming and fast performance everywhere.',
    color: '#f59e0b',
    gradient: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
    badge: '4K Ultra 60FPS',
    speedMetric: '< 18ms Latency',
    metricText: 'Zero Buffering CDN',
  },
  {
    id: 'creators',
    icon: <StarBorderIcon sx={{ fontSize: 26 }} />,
    title: 'For Every Creator',
    desc: 'Film, Music, Art — every talent belongs here.',
    color: '#8b5cf6',
    gradient: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
    badge: 'All Talents',
    tags: ['Actors', 'Directors', 'Musicians'],
    metricText: 'Zero Watermark / Royalty Free',
  },
  {
    id: 'ai-studio',
    icon: <AutoAwesomeRoundedIcon sx={{ fontSize: 26 }} />,
    title: 'Neural AI Studio',
    desc: 'Diffusion script-to-storyboard & automated character specs.',
    color: '#ec4899',
    gradient: 'linear-gradient(135deg, #ec4899 0%, #f43f5e 100%)',
    badge: 'Neural Diffusion',
    aiTags: ['Storyboards', 'Shotlists', 'LUTs'],
    aiMetric: '< 2.1s Gen Speed',
    metricText: 'Automated 4K Tokenizer',
  },
  {
    id: 'box-office',
    icon: <MovieCreationRoundedIcon sx={{ fontSize: 26 }} />,
    title: 'Cinema Box Office',
    desc: 'Direct-to-fan monetization with smart contracts & transparent royalties.',
    color: '#06b6d4',
    gradient: 'linear-gradient(135deg, #06b6d4 0%, #0ea5e9 100%)',
    badge: 'Direct-to-Fan',
    boxOfficeMetric: '$14.2M Volume',
    boxOfficeBadge: 'Instant Royalties',
    metricText: 'Zero Middleman Fees',
  },
];

const liveSlideItems = [
  { icon: '🎬', tag: 'AUDITION', title: 'Director Leo V. cast 3 Lead Roles for "Neon Horizon"', meta: 'Tokyo • 2m ago', color: '#07a4f5' },
  { icon: '⚡', tag: '4K PRORES', title: '60 FPS Cinema Master streamed with 14ms edge latency', meta: 'London Hub • 4m ago', color: '#f59e0b' },
  { icon: '🤖', tag: 'NEURAL AI', title: '32 Scene Storyboards & 3D LUTs synthesized in 1.9s', meta: 'Studio Lab • 6m ago', color: '#ec4899' },
  { icon: '🍿', tag: 'BOX OFFICE', title: 'Indie Feature "Solitude" reached $260K on Filmhook OTT', meta: 'Global OTT • 9m ago', color: '#06b6d4' },
  { icon: '🛡️', tag: 'VERIFIED ID', title: 'Studio Canal verified 16 SAG-AFTRA crew credentials', meta: 'Paris • 12m ago', color: '#10b981' },
  { icon: '🎵', tag: 'ATMOS MASTER', title: 'Original Cinema Score uploaded with zero watermark', meta: 'Berlin • 15m ago', color: '#8b5cf6' },
];

const CoreFeatures = () => {
  const { isDarkMode } = useTheme();

  const bgPrimary = isDarkMode ? '#050a14' : '#f8fafc';
  const textPrimary = isDarkMode ? '#ffffff' : '#0d1530';
  const textSecondary = isDarkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)';

  return (
    <Box sx={{ pt: { xs: 6, md: 8 }, pb: { xs: 3, md: 4 }, background: bgPrimary, position: 'relative', overflow: 'hidden' }}>
        {/* Glow Effects */}
        {isDarkMode && (
            <Box sx={{ position: 'absolute', top: '10%', right: '20%', width: '400px', height: '400px', background: 'radial-gradient(circle, rgba(7, 164, 245,0.15) 0%, transparent 70%)', zIndex: 0 }} />
        )}
        
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
            

            {/* Media Services Header */}
            <Box sx={{ textAlign: 'center', mb: 6 }}>
                {/* Cinema Ecosystem Kicker Badge */}
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1.2,
                    px: 2.2,
                    py: 0.6,
                    borderRadius: '100px',
                    background: isDarkMode ? 'rgba(7, 164, 245, 0.08)' : 'rgba(2, 132, 199, 0.08)',
                    border: isDarkMode ? '1px solid rgba(7, 164, 245, 0.25)' : '1px solid rgba(2, 132, 199, 0.2)',
                    backdropFilter: 'blur(12px)',
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: '#10b981',
                      boxShadow: '0 0 10px #10b981',
                      animation: 'beaconPill 1.4s infinite',
                      '@keyframes beaconPill': {
                        '0%, 100%': { transform: 'scale(1)', opacity: 1 },
                        '50%': { transform: 'scale(1.4)', opacity: 0.4 },
                      },
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      fontFamily: 'monospace',
                      fontWeight: 800,
                      letterSpacing: '0.12em',
                      color: isDarkMode ? '#38bdf8' : '#0284c7',
                      textTransform: 'uppercase',
                    }}
                  >
                    Filmhook Studios & Creative Hub
                  </Typography>
                </Box>

                <Typography variant="h3" sx={{ fontWeight: 900, color: textPrimary, fontSize: { xs: '2rem', md: '3rem' }, mb: 2, letterSpacing: '-0.02em' }}>
                    Explore Our{' '}
                    <Box
                      component="span"
                      sx={{
                        background: 'linear-gradient(135deg, #07a4f5 0%, #38bdf8 50%, #60a5fa 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        display: 'inline-block',
                      }}
                    >
                      Media Services
                    </Box>
                </Typography>
                <Typography sx={{ color: textSecondary, fontSize: { xs: '1rem', md: '1.1rem' }, maxWidth: '650px', mx: 'auto', lineHeight: 1.6 }}>
                    Powerful end-to-end platforms to stream 4K cinematic releases, discover verified global talent, and accelerate your production workflow.
                </Typography>
            </Box>

            {/* Two Large High-Level Animated Cinema Cards - Full Responsive 50/50 Grid */}
            <Grid container spacing={3} sx={{ mb: 5 }} alignItems="stretch">
                {/* 1. Film Streaming Cinema Card */}
                <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
                    <Box
                      component={motion.div}
                      whileHover={{ y: -6, scale: 1.01 }}
                      transition={{ duration: 0.3, ease: 'easeOut' }}
                      sx={{
                        width: '100%',
                        background: isDarkMode
                          ? 'linear-gradient(145deg, #0d1527 0%, #070e1b 100%)'
                          : 'linear-gradient(145deg, #ffffff 0%, #f0f7ff 100%)',
                        borderRadius: '20px',
                        border: isDarkMode ? '1.5px solid rgba(7, 164, 245, 0.2)' : '1.5px solid rgba(2, 132, 199, 0.18)',
                        p: { xs: 2, sm: 2.5 },
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: { xs: 2, sm: 2.5 },
                        height: '100%',
                        alignItems: 'center',
                        position: 'relative',
                        overflow: 'hidden',
                        boxShadow: isDarkMode
                          ? '0 16px 40px rgba(0, 0, 0, 0.5), 0 0 25px rgba(7, 164, 245, 0.08)'
                          : '0 12px 35px rgba(15, 23, 42, 0.06), 0 0 20px rgba(2, 132, 199, 0.06)',
                        transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
                        cursor: 'pointer',
                        '&:hover': {
                          borderColor: isDarkMode ? 'rgba(7, 164, 245, 0.55)' : 'rgba(2, 132, 199, 0.45)',
                          boxShadow: isDarkMode
                            ? '0 24px 50px rgba(0, 0, 0, 0.7), 0 0 35px rgba(7, 164, 245, 0.22)'
                            : '0 20px 45px rgba(15, 23, 42, 0.12), 0 0 25px rgba(2, 132, 199, 0.14)',
                          '& .card-img': {
                            transform: 'scale(1.06)',
                          },
                          '& .play-overlay-btn': {
                            transform: 'translate(-50%, -50%) scale(1.12)',
                            background: 'rgba(7, 164, 245, 0.95)',
                            boxShadow: '0 0 25px rgba(7, 164, 245, 0.8)',
                          },
                          '& .cta-arrow': {
                            transform: 'translateX(4px)',
                          },
                        },
                      }}
                    >
                        {/* Interactive Cinema Poster Screen (Left) - Compact Proportions */}
                        <Box
                          sx={{
                            width: { xs: '100%', sm: '42%', lg: '38%' },
                            height: { xs: 170, sm: 190, md: 205 },
                            position: 'relative',
                            borderRadius: '14px',
                            overflow: 'hidden',
                            flexShrink: 0,
                            boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
                          }}
                        >
                            <Box
                                component="img"
                                className="card-img"
                                src="https://images.unsplash.com/photo-1616530940355-351fabd9524b?q=80&w=700&auto=format&fit=crop"
                                alt="Film Streaming"
                                sx={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  transition: 'transform 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
                                }}
                            />

                            {/* Anamorphic Gradient Scrim */}
                            <Box
                              sx={{
                                position: 'absolute',
                                inset: 0,
                                background: 'linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.08) 40%, rgba(0,0,0,0.85) 100%)',
                              }}
                            />

                            {/* Top Left: Live Status Badge with Animated Ping */}
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 8,
                                left: 8,
                                background: 'rgba(225, 29, 72, 0.92)',
                                color: 'white',
                                px: 1,
                                py: 0.3,
                                borderRadius: '6px',
                                fontSize: '0.62rem',
                                fontWeight: 800,
                                letterSpacing: '0.06em',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.6,
                                backdropFilter: 'blur(6px)',
                                boxShadow: '0 2px 10px rgba(225, 29, 72, 0.4)',
                              }}
                            >
                                <Box
                                  sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    background: 'white',
                                    animation: 'livePing 1.2s infinite',
                                    '@keyframes livePing': {
                                      '0%, 100%': { transform: 'scale(1)', opacity: 1 },
                                      '50%': { transform: 'scale(1.4)', opacity: 0.5 },
                                    },
                                  }}
                                />
                                LIVE 4K
                            </Box>

                            {/* Top Right: Viewer Counter */}
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                background: 'rgba(0, 0, 0, 0.65)',
                                color: 'rgba(255, 255, 255, 0.9)',
                                px: 0.9,
                                py: 0.3,
                                borderRadius: '6px',
                                fontSize: '0.62rem',
                                fontWeight: 700,
                                fontFamily: 'monospace',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                backdropFilter: 'blur(6px)',
                                border: '1px solid rgba(255,255,255,0.12)',
                              }}
                            >
                                <VisibilityRoundedIcon sx={{ fontSize: '0.75rem', color: '#38bdf8' }} />
                                8.4K
                            </Box>

                            {/* Center Floating Glassmorphic Play Target Button */}
                            <Box
                              className="play-overlay-btn"
                              sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%) scale(1)',
                                width: 44,
                                height: 44,
                                borderRadius: '50%',
                                background: 'rgba(7, 164, 245, 0.85)',
                                backdropFilter: 'blur(8px)',
                                border: '1.5px solid rgba(255, 255, 255, 0.6)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyCenter: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 6px 20px rgba(7, 164, 245, 0.5)',
                                transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                              }}
                            >
                                <PlayArrowRoundedIcon sx={{ fontSize: '1.6rem', color: '#ffffff', ml: 0.2 }} />
                            </Box>

                            {/* Bottom Bar: Animated Soundwave Visualizer + Specs */}
                            <Box
                              sx={{
                                position: 'absolute',
                                bottom: 8,
                                left: 8,
                                right: 8,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                              }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: '12px' }}>
                                    {[50, 90, 40, 100, 70].map((h, i) => (
                                      <motion.div
                                        key={i}
                                        animate={{ height: [`${h * 0.3}%`, `${h}%`, `${h * 0.5}%`] }}
                                        transition={{ duration: 0.45 + i * 0.1, repeat: Infinity, repeatType: 'reverse' }}
                                        style={{ width: '2.5px', background: '#38bdf8', borderRadius: '2px' }}
                                      />
                                    ))}
                                    <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.6rem', fontFamily: 'monospace', ml: 0.6, fontWeight: 700 }}>
                                      ATMOS
                                    </Typography>
                                </Box>

                                <Box sx={{ background: 'rgba(7, 164, 245, 0.25)', border: '1px solid rgba(7, 164, 245, 0.45)', px: 0.8, py: 0.15, borderRadius: '4px', fontSize: '0.58rem', fontFamily: 'monospace', color: '#38bdf8', fontWeight: 800 }}>
                                    60 FPS
                                </Box>
                            </Box>
                        </Box>

                        {/* Content Info (Right) - Compact & Elegant */}
                        <Box sx={{ flex: 1, width: '100%', display: 'flex', flexDirection: 'column' }}>
                            {/* Animated Icon Pill */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1 }}>
                                <Box
                                  sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: '10px',
                                    background: 'linear-gradient(135deg, #07a4f5 0%, #0284c7 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 4px 14px rgba(7, 164, 245, 0.35)',
                                  }}
                                >
                                    <VideocamIcon sx={{ color: 'white', fontSize: '1.15rem' }} />
                                </Box>
                                <Typography sx={{ fontSize: '0.65rem', fontFamily: 'monospace', color: isDarkMode ? '#38bdf8' : '#0284c7', fontWeight: 800, letterSpacing: '0.1em' }}>
                                    STREAMING & BOX OFFICE
                                </Typography>
                            </Box>

                            <Typography sx={{ color: textPrimary, fontWeight: 900, fontSize: { xs: '1.15rem', sm: '1.25rem' }, mb: 0.6, letterSpacing: '-0.01em' }}>
                                Film Streaming
                            </Typography>
                            <Typography sx={{ color: textSecondary, fontSize: '0.82rem', lineHeight: 1.5, mb: 1.6 }}>
                                Watch and enjoy curated indie movies, award-winning web series, and studio dailies in pristine 4K HDR quality with zero buffering.
                            </Typography>

                            {/* Feature Capability Chips */}
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mb: 2 }}>
                                {['🎬 4K HDR Releases', '⚡ Zero-Buffer CDN', '🍿 Direct-to-Fan'].map((tag) => (
                                  <Box
                                    key={tag}
                                    sx={{
                                      px: 1,
                                      py: 0.25,
                                      borderRadius: '6px',
                                      background: isDarkMode ? 'rgba(7, 164, 245, 0.08)' : 'rgba(2, 132, 199, 0.08)',
                                      border: isDarkMode ? '1px solid rgba(7, 164, 245, 0.2)' : '1px solid rgba(2, 132, 199, 0.18)',
                                      fontSize: '0.65rem',
                                      fontFamily: '"Inter", sans-serif',
                                      fontWeight: 600,
                                      color: isDarkMode ? '#bae6fd' : '#0369a1',
                                    }}
                                  >
                                    {tag}
                                  </Box>
                                ))}
                            </Box>

                            {/* Animated CTA Button */}
                            <Button
                              component={Link}
                              to="/services/film-streaming"
                              endIcon={<ArrowForwardIcon className="cta-arrow" sx={{ fontSize: '1rem', transition: 'transform 0.25s ease' }} />}
                              sx={{
                                alignSelf: 'flex-start',
                                px: 2.2,
                                py: 0.65,
                                borderRadius: '10px',
                                background: isDarkMode
                                  ? 'linear-gradient(135deg, rgba(7, 164, 245, 0.15) 0%, rgba(7, 164, 245, 0.25) 100%)'
                                  : 'linear-gradient(135deg, #07a4f5 0%, #0284c7 100%)',
                                color: isDarkMode ? '#38bdf8' : '#ffffff',
                                border: isDarkMode ? '1px solid rgba(7, 164, 245, 0.4)' : 'none',
                                textTransform: 'none',
                                fontWeight: 800,
                                fontSize: '0.8rem',
                                letterSpacing: '0.03em',
                                boxShadow: isDarkMode ? '0 4px 14px rgba(7, 164, 245, 0.2)' : '0 4px 15px rgba(7, 164, 245, 0.28)',
                                transition: 'all 0.25s ease',
                                '&:hover': {
                                  background: isDarkMode ? 'rgba(7, 164, 245, 0.35)' : '#0284c7',
                                  transform: 'translateY(-1px)',
                                  boxShadow: '0 6px 20px rgba(7, 164, 245, 0.4)',
                                },
                              }}
                            >
                                Explore Streaming
                            </Button>
                        </Box>
                    </Box>
                </Grid>
                
                {/* 2. Talent Discovery Cinema Card */}
                <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
                    <Box
                      component={motion.div}
                      whileHover={{ y: -6, scale: 1.01 }}
                      transition={{ duration: 0.3, ease: 'easeOut' }}
                      sx={{
                        width: '100%',
                        background: isDarkMode
                          ? 'linear-gradient(145deg, #110d24 0%, #0a071a 100%)'
                          : 'linear-gradient(145deg, #ffffff 0%, #fbf8ff 100%)',
                        borderRadius: '20px',
                        border: isDarkMode ? '1.5px solid rgba(168, 85, 247, 0.22)' : '1.5px solid rgba(147, 51, 234, 0.18)',
                        p: { xs: 2, sm: 2.5 },
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: { xs: 2, sm: 2.5 },
                        height: '100%',
                        alignItems: 'center',
                        position: 'relative',
                        overflow: 'hidden',
                        boxShadow: isDarkMode
                          ? '0 16px 40px rgba(0, 0, 0, 0.5), 0 0 25px rgba(168, 85, 247, 0.08)'
                          : '0 12px 35px rgba(15, 23, 42, 0.06), 0 0 20px rgba(147, 51, 234, 0.06)',
                        transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
                        cursor: 'pointer',
                        '&:hover': {
                          borderColor: isDarkMode ? 'rgba(168, 85, 247, 0.6)' : 'rgba(147, 51, 234, 0.45)',
                          boxShadow: isDarkMode
                            ? '0 24px 50px rgba(0, 0, 0, 0.7), 0 0 35px rgba(168, 85, 247, 0.25)'
                            : '0 20px 45px rgba(15, 23, 42, 0.12), 0 0 25px rgba(147, 51, 234, 0.16)',
                          '& .card-img': {
                            transform: 'scale(1.06)',
                          },
                          '& .projector-beam': {
                            opacity: 0.85,
                            transform: 'rotate(-25deg) scale(1.15)',
                          },
                          '& .cta-arrow': {
                            transform: 'translateX(4px)',
                          },
                        },
                      }}
                    >
                        {/* Interactive Projector & Talent Stage (Left) - Compact Proportions */}
                        <Box
                          sx={{
                            width: { xs: '100%', sm: '42%', lg: '38%' },
                            height: { xs: 170, sm: 190, md: 205 },
                            position: 'relative',
                            borderRadius: '14px',
                            overflow: 'hidden',
                            flexShrink: 0,
                            boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
                          }}
                        >
                            <Box
                                component="img"
                                className="card-img"
                                src="https://images.unsplash.com/photo-1478720568477-152d9b164e26?q=80&w=700&auto=format&fit=crop"
                                alt="Talent Discovery"
                                sx={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  transition: 'transform 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
                                }}
                            />

                            {/* Volumetric Projector Beam Light Ray */}
                            <Box
                              className="projector-beam"
                              sx={{
                                position: 'absolute',
                                top: '-20%',
                                left: '30%',
                                width: '140%',
                                height: '140%',
                                background: 'radial-gradient(ellipse at 15% 45%, rgba(192, 132, 252, 0.45) 0%, rgba(168, 85, 247, 0.1) 40%, transparent 70%)',
                                transform: 'rotate(-25deg)',
                                pointerEvents: 'none',
                                filter: 'blur(14px)',
                                opacity: 0.55,
                                transition: 'all 0.5s ease',
                              }}
                            />

                            {/* Anamorphic Gradient Scrim */}
                            <Box
                              sx={{
                                position: 'absolute',
                                inset: 0,
                                background: 'linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.08) 40%, rgba(15, 8, 30, 0.9) 100%)',
                              }}
                            />

                            {/* Top Left: Verified Casting Badge */}
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 8,
                                left: 8,
                                background: 'linear-gradient(135deg, #9333ea 0%, #7e22ce 100%)',
                                color: 'white',
                                px: 1,
                                py: 0.3,
                                borderRadius: '6px',
                                fontSize: '0.62rem',
                                fontWeight: 800,
                                letterSpacing: '0.06em',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.6,
                                backdropFilter: 'blur(6px)',
                                boxShadow: '0 2px 10px rgba(147, 51, 234, 0.4)',
                              }}
                            >
                                <StarsRoundedIcon sx={{ fontSize: '0.8rem', color: '#fbbf24' }} />
                                CASTING
                            </Box>

                            {/* Top Right: Profile Counter */}
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                background: 'rgba(0, 0, 0, 0.65)',
                                color: 'rgba(255, 255, 255, 0.9)',
                                px: 0.9,
                                py: 0.3,
                                borderRadius: '6px',
                                fontSize: '0.62rem',
                                fontWeight: 700,
                                fontFamily: 'monospace',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                backdropFilter: 'blur(6px)',
                                border: '1px solid rgba(255,255,255,0.12)',
                              }}
                            >
                                <PeopleIcon sx={{ fontSize: '0.75rem', color: '#c084fc' }} />
                                14.8K
                            </Box>

                            {/* Center Floating Glassmorphic Talent Icon Target */}
                            <Box
                              sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: 44,
                                height: 44,
                                borderRadius: '50%',
                                background: 'rgba(147, 51, 234, 0.85)',
                                backdropFilter: 'blur(8px)',
                                border: '1.5px solid rgba(255, 255, 255, 0.6)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 6px 20px rgba(147, 51, 234, 0.5)',
                              }}
                            >
                                <MovieCreationRoundedIcon sx={{ fontSize: '1.5rem', color: '#ffffff' }} />
                            </Box>

                            {/* Bottom Bar: Talent Category Indicators */}
                            <Box
                              sx={{
                                position: 'absolute',
                                bottom: 8,
                                left: 8,
                                right: 8,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                              }}
                            >
                                <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.6rem', fontFamily: 'monospace', fontWeight: 700 }}>
                                    ACTORS • DIRECTORS
                                </Typography>
                                <Box sx={{ background: 'rgba(168, 85, 247, 0.3)', border: '1px solid rgba(168, 85, 247, 0.5)', px: 0.8, py: 0.15, borderRadius: '4px', fontSize: '0.58rem', fontFamily: 'monospace', color: '#ffffff', fontWeight: 800 }}>
                                    98.4%
                                </Box>
                            </Box>
                        </Box>

                        {/* Content Info (Right) - Compact & Elegant */}
                        <Box sx={{ flex: 1, width: '100%', display: 'flex', flexDirection: 'column' }}>
                            {/* Animated Icon Pill */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1 }}>
                                <Box
                                  sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: '10px',
                                    background: 'linear-gradient(135deg, #a855f7 0%, #7e22ce 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 4px 14px rgba(168, 85, 247, 0.35)',
                                  }}
                                >
                                    <PeopleIcon sx={{ color: 'white', fontSize: '1.15rem' }} />
                                </Box>
                                <Typography sx={{ fontSize: '0.65rem', fontFamily: 'monospace', color: isDarkMode ? '#c084fc' : '#7e22ce', fontWeight: 800, letterSpacing: '0.1em' }}>
                                    GLOBAL CASTING DIRECTORY
                                </Typography>
                            </Box>

                            <Typography sx={{ color: textPrimary, fontWeight: 900, fontSize: { xs: '1.15rem', sm: '1.25rem' }, mb: 0.6, letterSpacing: '-0.01em' }}>
                                Talent Discovery
                            </Typography>
                            <Typography sx={{ color: textSecondary, fontSize: '0.82rem', lineHeight: 1.5, mb: 1.6 }}>
                                Connect with verified actors, directors, cinematographers, and voice talent across 100+ countries with 1-tap video auditions and showreels.
                            </Typography>

                            {/* Feature Capability Chips */}
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mb: 2 }}>
                                {['🎭 Audition Reels', '🤖 AI Smart Match', '📍 Location Scouting'].map((tag) => (
                                  <Box
                                    key={tag}
                                    sx={{
                                      px: 1,
                                      py: 0.25,
                                      borderRadius: '6px',
                                      background: isDarkMode ? 'rgba(168, 85, 247, 0.08)' : 'rgba(147, 51, 234, 0.08)',
                                      border: isDarkMode ? '1px solid rgba(168, 85, 247, 0.22)' : '1px solid rgba(147, 51, 234, 0.18)',
                                      fontSize: '0.65rem',
                                      fontFamily: '"Inter", sans-serif',
                                      fontWeight: 600,
                                      color: isDarkMode ? '#e9d5ff' : '#6b21a8',
                                    }}
                                  >
                                    {tag}
                                  </Box>
                                ))}
                            </Box>

                            {/* Animated CTA Button */}
                            <Button
                              component={Link}
                              to="/services/talent-discovery"
                              endIcon={<ArrowForwardIcon className="cta-arrow" sx={{ fontSize: '1rem', transition: 'transform 0.25s ease' }} />}
                              sx={{
                                alignSelf: 'flex-start',
                                px: 2.2,
                                py: 0.65,
                                borderRadius: '10px',
                                background: isDarkMode
                                  ? 'linear-gradient(135deg, rgba(168, 85, 247, 0.15) 0%, rgba(168, 85, 247, 0.25) 100%)'
                                  : 'linear-gradient(135deg, #a855f7 0%, #7e22ce 100%)',
                                color: isDarkMode ? '#c084fc' : '#ffffff',
                                border: isDarkMode ? '1px solid rgba(168, 85, 247, 0.4)' : 'none',
                                textTransform: 'none',
                                fontWeight: 800,
                                fontSize: '0.8rem',
                                letterSpacing: '0.03em',
                                boxShadow: isDarkMode ? '0 4px 14px rgba(168, 85, 247, 0.2)' : '0 4px 15px rgba(168, 85, 247, 0.28)',
                                transition: 'all 0.25s ease',
                                '&:hover': {
                                  background: isDarkMode ? 'rgba(168, 85, 247, 0.35)' : '#7e22ce',
                                  transform: 'translateY(-1px)',
                                  boxShadow: '0 6px 20px rgba(168, 85, 247, 0.4)',
                                },
                              }}
                            >
                                Discover Talent
                            </Button>
                        </Box>
                    </Box>
                </Grid>
            </Grid>

            {/* Bottom Features: Dynamic, Animated & Attractive Cinema Metrics Dock */}
            <Box
              sx={{
                background: isDarkMode
                  ? 'linear-gradient(135deg, rgba(16, 23, 38, 0.95) 0%, rgba(8, 14, 26, 0.98) 100%)'
                  : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                borderRadius: '28px',
                border: isDarkMode ? '1.5px solid rgba(255,255,255,0.08)' : '1.5px solid rgba(0,0,0,0.07)',
                boxShadow: isDarkMode
                  ? '0 24px 60px rgba(0, 0, 0, 0.7), 0 0 40px rgba(7, 164, 245, 0.08)'
                  : '0 20px 60px rgba(15, 23, 42, 0.08), 0 1px 3px rgba(0,0,0,0.05)',
                p: { xs: 2.5, sm: 3, md: 4 },
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              {/* Header Ticker Strip */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 3,
                  pb: 2,
                  borderBottom: isDarkMode
                    ? '1px solid rgba(255, 255, 255, 0.08)'
                    : '1px solid rgba(0, 0, 0, 0.06)',
                  flexWrap: 'wrap',
                  gap: 1.5,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: '#10b981',
                      boxShadow: '0 0 10px #10b981',
                      animation: 'pulse 1.5s infinite',
                    }}
                  />
                  <Typography
                    sx={{
                      fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                      fontSize: '0.72rem',
                      fontWeight: 800,
                      letterSpacing: '0.08em',
                      color: isDarkMode ? '#94a3b8' : '#64748b',
                    }}
                  >
                    LIVE NETWORK • 99.98% GLOBAL RELIABILITY • AUDITIONS & COLLABORATION
                  </Typography>
                </Box>

                <Chip
                  icon={<AutoAwesomeRoundedIcon sx={{ fontSize: '13px !important', color: '#07a4f5' }} />}
                  label="Verified Creative Ecosystem"
                  size="small"
                  sx={{
                    fontWeight: 800,
                    fontSize: '0.68rem',
                    bgcolor: isDarkMode ? 'rgba(7, 164, 245, 0.12)' : 'rgba(7, 164, 245, 0.08)',
                    color: '#07a4f5',
                    border: '1px solid rgba(7, 164, 245, 0.25)',
                  }}
                />
              </Box>

              <Grid container spacing={2.5}>
                {bottomFeatures.map((feat, i) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
                    <Box
                      component={motion.div}
                      whileHover={{ y: -7, scale: 1.02 }}
                      transition={{ duration: 0.25, ease: 'easeOut' }}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        height: '100%',
                        p: 2.8,
                        borderRadius: '22px',
                        background: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : '#ffffff',
                        border: isDarkMode
                          ? '1px solid rgba(255, 255, 255, 0.07)'
                          : '1px solid rgba(0, 0, 0, 0.06)',
                        boxShadow: isDarkMode
                          ? '0 6px 24px rgba(0, 0, 0, 0.35)'
                          : '0 6px 22px rgba(15, 23, 42, 0.05)',
                        transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                        cursor: 'default',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: '18%',
                          right: '18%',
                          height: '3px',
                          background: feat.gradient,
                          borderRadius: '0 0 4px 4px',
                          opacity: 0.75,
                          transition: 'all 0.35s ease',
                        },
                        '&:hover': {
                          borderColor: feat.color,
                          boxShadow: `0 16px 40px ${feat.color}25`,
                          background: isDarkMode ? 'rgba(255, 255, 255, 0.055)' : '#ffffff',
                          '&::before': {
                            left: '5%',
                            right: '5%',
                            height: '4px',
                            boxShadow: `0 0 14px ${feat.color}`,
                            opacity: 1,
                          },
                        },
                      }}
                    >
                      {/* Top Row: Icon Badge & Metric Badge */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Box
                          sx={{
                            width: 52,
                            height: 52,
                            borderRadius: '16px',
                            background: feat.gradient,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#ffffff',
                            boxShadow: `0 8px 24px ${feat.color}50`,
                            transition: 'transform 0.3s ease',
                            '&:hover': {
                              transform: 'scale(1.1) rotate(6deg)',
                            },
                          }}
                        >
                          {feat.icon}
                        </Box>

                        <Chip
                          label={feat.badge}
                          size="small"
                          sx={{
                            fontWeight: 800,
                            fontSize: '0.68rem',
                            bgcolor: `${feat.color}15`,
                            color: feat.color,
                            border: `1px solid ${feat.color}35`,
                            height: 24,
                            px: 0.5,
                          }}
                        />
                      </Box>

                      {/* Middle: Title & Description */}
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          sx={{
                            color: textPrimary,
                            fontWeight: 800,
                            fontSize: '1.08rem',
                            mb: 0.8,
                            transition: 'color 0.2s ease',
                          }}
                        >
                          {feat.title}
                        </Typography>
                        <Typography
                          sx={{
                            color: textSecondary,
                            fontSize: '0.85rem',
                            lineHeight: 1.6,
                          }}
                        >
                          {feat.desc}
                        </Typography>
                      </Box>

                      {/* Bottom Micro Visual Element */}
                      <Box
                        sx={{
                          pt: 1.8,
                          borderTop: isDarkMode
                            ? '1px solid rgba(255, 255, 255, 0.06)'
                            : '1px solid rgba(0, 0, 0, 0.06)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          flexWrap: 'wrap',
                          gap: 1,
                        }}
                      >
                        {/* 1. Community: Avatar Stack & Live Pulsing Beacon */}
                        {feat.avatars && (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {feat.avatars.map((av, idx) => (
                              <Avatar
                                key={idx}
                                sx={{
                                  width: 24,
                                  height: 24,
                                  fontSize: '0.6rem',
                                  fontWeight: 800,
                                  bgcolor: av.bg,
                                  border: `2px solid ${isDarkMode ? '#050a14' : '#ffffff'}`,
                                  ml: idx === 0 ? 0 : -0.8,
                                }}
                              >
                                {av.label}
                              </Avatar>
                            ))}
                            <Box sx={{ display: 'flex', alignItems: 'center', ml: 1, gap: 0.6 }}>
                              <Box
                                sx={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: '50%',
                                  bgcolor: '#10b981',
                                  boxShadow: '0 0 8px #10b981',
                                  animation: 'pulse 1.4s infinite',
                                }}
                              />
                              <Typography sx={{ color: feat.color, fontSize: '0.72rem', fontWeight: 800 }}>
                                +100K Live
                              </Typography>
                            </Box>
                          </Box>
                        )}

                        {/* 2. Security: Shield Check & SAG-AFTRA Ready */}
                        {feat.secBadge && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                            <CheckCircleRoundedIcon sx={{ fontSize: 16, color: '#10b981' }} />
                            <Typography sx={{ color: '#10b981', fontSize: '0.72rem', fontWeight: 800 }}>
                              {feat.secBadge}
                            </Typography>
                          </Box>
                        )}

                        {/* 3. Speed: 4K Equalizer Bars */}
                        {feat.speedMetric && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: 14 }}>
                              <Box sx={{ width: 2.5, bgcolor: '#f59e0b', borderRadius: 1, animation: 'eqPulse1 0.7s ease-in-out infinite' }} />
                              <Box sx={{ width: 2.5, bgcolor: '#f59e0b', borderRadius: 1, animation: 'eqPulse2 0.55s ease-in-out infinite' }} />
                              <Box sx={{ width: 2.5, bgcolor: '#f59e0b', borderRadius: 1, animation: 'eqPulse3 0.85s ease-in-out infinite' }} />
                            </Box>
                            <Typography sx={{ color: '#f59e0b', fontSize: '0.72rem', fontWeight: 800 }}>
                              {feat.speedMetric}
                            </Typography>
                          </Box>
                        )}

                        {/* 4. Creator: Talent Tags */}
                        {feat.tags && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                            {feat.tags.map((t, idx) => (
                              <Typography
                                key={idx}
                                sx={{
                                  fontSize: '0.65rem',
                                  fontWeight: 800,
                                  color: feat.color,
                                  bgcolor: `${feat.color}15`,
                                  px: 0.8,
                                  py: 0.25,
                                  borderRadius: '6px',
                                }}
                              >
                                {t}
                              </Typography>
                            ))}
                          </Box>
                        )}

                        {/* 5. AI Studio: Neural Tags */}
                        {feat.aiTags && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                            {feat.aiTags.map((t, idx) => (
                              <Typography
                                key={idx}
                                sx={{
                                  fontSize: '0.65rem',
                                  fontWeight: 800,
                                  color: feat.color,
                                  bgcolor: `${feat.color}15`,
                                  px: 0.8,
                                  py: 0.25,
                                  borderRadius: '6px',
                                }}
                              >
                                {t}
                              </Typography>
                            ))}
                          </Box>
                        )}

                        {/* 6. Box Office: Instant Royalties */}
                        {feat.boxOfficeMetric && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                            <MovieCreationRoundedIcon sx={{ fontSize: 16, color: '#06b6d4' }} />
                            <Typography sx={{ color: '#06b6d4', fontSize: '0.72rem', fontWeight: 800 }}>
                              {feat.boxOfficeMetric}
                            </Typography>
                          </Box>
                        )}

                        <Typography
                          variant="caption"
                          sx={{
                            color: isDarkMode ? '#94a3b8' : '#64748b',
                            fontSize: '0.68rem',
                            fontWeight: 700,
                          }}
                        >
                          {feat.metricText}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {/* Dynamic Sliding Activity Stream ("Live Cinema Telemetry") */}
              <Box
                sx={{
                  mt: 3.5,
                  pt: 2.5,
                  borderTop: isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.06)',
                  overflow: 'hidden',
                  position: 'relative',
                  '&::before, &::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    width: { xs: 40, md: 80 },
                    zIndex: 2,
                    pointerEvents: 'none',
                  },
                  '&::before': {
                    left: 0,
                    background: isDarkMode
                      ? 'linear-gradient(to right, #080e1a, transparent)'
                      : 'linear-gradient(to right, #ffffff, transparent)',
                  },
                  '&::after': {
                    right: 0,
                    background: isDarkMode
                      ? 'linear-gradient(to left, #080e1a, transparent)'
                      : 'linear-gradient(to left, #ffffff, transparent)',
                  },
                }}
              >
                {/* Stream Sub-header */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.8, px: 0.5, flexWrap: 'wrap', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SensorsRoundedIcon sx={{ fontSize: 16, color: '#07a4f5' }} />
                    <Typography
                      sx={{
                        fontSize: '0.7rem',
                        fontWeight: 800,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color: isDarkMode ? '#94a3b8' : '#64748b',
                        fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                      }}
                    >
                      LIVE NETWORK TELEMETRY • REAL-TIME AUDITIONS & PRODUCTIONS
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      color: '#10b981',
                      bgcolor: 'rgba(16, 185, 129, 0.1)',
                      px: 1.2,
                      py: 0.3,
                      borderRadius: '999px',
                      border: '1px solid rgba(16, 185, 129, 0.25)',
                    }}
                  >
                    ● 184 Global Nodes Online
                  </Typography>
                </Box>

                {/* Animated Marquee Strip */}
                <Box
                  sx={{
                    display: 'flex',
                    width: 'max-content',
                    animation: 'marqueeSlide 30s linear infinite',
                    '&:hover': {
                      animationPlayState: 'paused',
                    },
                    '@keyframes marqueeSlide': {
                      '0%': { transform: 'translateX(0)' },
                      '100%': { transform: 'translateX(-50%)' },
                    },
                    '@keyframes eqPulse1': {
                      '0%, 100%': { height: 4 },
                      '50%': { height: 14 },
                    },
                    '@keyframes eqPulse2': {
                      '0%, 100%': { height: 14 },
                      '50%': { height: 5 },
                    },
                    '@keyframes eqPulse3': {
                      '0%, 100%': { height: 7 },
                      '50%': { height: 16 },
                    },
                  }}
                >
                  {[...liveSlideItems, ...liveSlideItems].map((item, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.2,
                        mx: 1,
                        px: 1.8,
                        py: 0.8,
                        borderRadius: '999px',
                        background: isDarkMode ? 'rgba(255, 255, 255, 0.04)' : '#f1f5f9',
                        border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.06)',
                        transition: 'all 0.25s ease',
                        cursor: 'pointer',
                        '&:hover': {
                          background: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : '#e2e8f0',
                          borderColor: item.color,
                          transform: 'translateY(-2px)',
                          boxShadow: `0 4px 15px ${item.color}30`,
                        },
                      }}
                    >
                      <Typography sx={{ fontSize: '0.95rem', lineHeight: 1 }}>{item.icon}</Typography>
                      <Chip
                        label={item.tag}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '0.62rem',
                          fontWeight: 900,
                          bgcolor: `${item.color}20`,
                          color: item.color,
                          border: `1px solid ${item.color}40`,
                          letterSpacing: '0.04em',
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: '0.78rem',
                          fontWeight: 600,
                          color: textPrimary,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {item.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.68rem',
                          fontWeight: 500,
                          color: isDarkMode ? '#94a3b8' : '#64748b',
                          whiteSpace: 'nowrap',
                          pl: 0.5,
                          borderLeft: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
                        }}
                      >
                        {item.meta}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>

        </Container>
    </Box>
  );
};

export default CoreFeatures;
